import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import '../styles/Profile.css';

const API_URL = process.env.REACT_APP_API_URL;
const MAX_PHOTO_SIZE = 5 * 1024 * 1024;

/* ------------------------------------------------------------------ */
/* İkonlar                                                             */
/* ------------------------------------------------------------------ */
const ICONS = {
  user: (<><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></>),
  lock: (<><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></>),
  clock: (<><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></>),
  camera: (<><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" /><circle cx="12" cy="13" r="4" /></>),
  check: <polyline points="20 6 9 17 4 12" />,
  dot: <circle cx="12" cy="12" r="3" />,
  eye: (<><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></>),
  eyeOff: (<><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" /><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" /><path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" /><line x1="1" y1="1" x2="23" y2="23" /></>),
};

const Icon = ({ name, className = '' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {ICONS[name]}
  </svg>
);

/* ------------------------------------------------------------------ */
/* Şifre alanı: göz simgesiyle göster / gizle                          */
/* (Login sayfasında da aynen kullanılabilir)                          */
/* ------------------------------------------------------------------ */
const PasswordField = ({ id, label, value, onChange, autoComplete }) => {
  const [visible, setVisible] = useState(false);
  return (
    <div className="pf-field">
      <label className="pf-label" htmlFor={id}>{label}</label>
      <div className="pf-pw">
        <input
          id={id}
          type={visible ? 'text' : 'password'}
          className="pf-input pf-pw-input"
          value={value}
          onChange={onChange}
          autoComplete={autoComplete}
        />
        <button
          type="button"
          className="pf-eye"
          onMouseDown={(e) => e.preventDefault()} /* imleç alanda kalsın */
          onClick={() => setVisible((v) => !v)}
          aria-label={visible ? 'Şifreyi gizle' : 'Şifreyi göster'}
          aria-pressed={visible}
          title={visible ? 'Şifreyi gizle' : 'Şifreyi göster'}
        >
          <Icon name={visible ? 'eyeOff' : 'eye'} className="pf-eye-icon" />
        </button>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Sabitler ve doğrulama kuralları                                     */
/* ------------------------------------------------------------------ */
const ROLES = ['Öğrenci', 'Öğretmen', 'Diğer'];
const CLASS_YEARS = [
  'Hazırlık', '1. sınıf', '2. sınıf', '3. sınıf', '4. sınıf', '5. sınıf', '6. sınıf',
  'Yüksek lisans', 'Doktora', 'Mezun',
];

// Rol bir kez atandıktan sonra kullanıcı tarafından değiştirilemez.
// (Değişiklik yönetici onayıyla yapılmalı. Esnetmek istersen false yap.)
const LOCK_ROLE_AFTER_SET = true;

// Öğrenci rolünde zorunlu olan akademik alanlar
const STUDENT_REQUIRED = ['university', 'faculty', 'department', 'classYear'];

const MIN_AGE = 13;
const MAX_AGE = 100;
const TEXT_KEYS = ['university', 'faculty', 'department'];
const TEXT_RE = /^[\p{L}\p{N}][\p{L}\p{N} .,'’&()/-]*$/u;
const PHONE_TR_RE = /^(?:\+90|90|0)?5\d{9}$/;

// Form alanları: tekrar eden JSX yerine tek yerden yönetiliyor
const FIELDS = [
  { name: 'first_name', label: 'Ad', disabled: true, autoComplete: 'given-name' },
  { name: 'last_name', label: 'Soyad', disabled: true, autoComplete: 'family-name' },
  { name: 'email', label: 'E-posta', type: 'email', disabled: true, full: true, autoComplete: 'email' },
  { name: 'userRole', label: 'Rol', full: true, options: ROLES, placeholder: 'Rol seçin' },
  { name: 'phoneNumber', label: 'Telefon numarası', type: 'tel', placeholder: 'Örn. 0532 123 45 67', autoComplete: 'tel', inputMode: 'tel' },
  { name: 'birthDate', label: 'Doğum tarihi', type: 'date', autoComplete: 'bday' },
  { name: 'university', label: 'Üniversite', full: true },
  { name: 'faculty', label: 'Fakülte' },
  { name: 'department', label: 'Bölüm' },
  { name: 'classYear', label: 'Sınıf', options: CLASS_YEARS, placeholder: 'Sınıf seçin' },
];

// Kullanıcının değiştirebildiği alanlar (kaydedilmemiş değişiklik kontrolü için)
const EDITABLE_KEYS = FIELDS.filter((f) => !f.disabled).map((f) => f.name);

const pad = (n) => String(n).padStart(2, '0');
const localISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const todayISO = () => localISO(new Date());
const yearsAgoISO = (n) => {
  const d = new Date();
  d.setFullYear(d.getFullYear() - n);
  return localISO(d);
};
// API tarihi "2001-04-05T00:00:00.000Z" gibi dönebilir; <input type="date"> yalnızca YYYY-AA-GG kabul eder
const toDateInput = (value) => (value ? String(value).slice(0, 10) : '');

const isValidISODate = (value) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const [y, m, d] = value.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  return dt.getFullYear() === y && dt.getMonth() === m - 1 && dt.getDate() === d;
};

const ageOn = (iso) => {
  const [y, m, d] = iso.split('-').map(Number);
  const t = new Date();
  let age = t.getFullYear() - y;
  if (t.getMonth() + 1 < m || (t.getMonth() + 1 === m && t.getDate() < d)) age -= 1;
  return age;
};

const normalizeText = (v) => String(v || '').replace(/\s+/g, ' ').trim();
const normalizePhone = (v) => {
  const raw = String(v || '').trim();
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  return raw.startsWith('+') ? `+${digits}` : digits;
};
const normalizeField = (name, value) => {
  if (name === 'phoneNumber') return normalizePhone(value);
  if (TEXT_KEYS.includes(name)) return normalizeText(value);
  return String(value || '').trim();
};

// Tek bir alanı doğrular; hata yoksa '' döner
const validateField = (name, value, values, saved) => {
  const isStudent = values.userRole === 'Öğrenci';
  const required = name === 'userRole' || (isStudent && STUDENT_REQUIRED.includes(name));

  if (!value) {
    if (name === 'userRole') return 'Lütfen bir rol seçin.';
    return required ? 'Öğrenci rolü için bu alan zorunlu.' : '';
  }

  switch (name) {
    case 'userRole':
      return ROLES.includes(value) ? '' : 'Geçersiz bir rol seçildi.';

    case 'phoneNumber':
      if (PHONE_TR_RE.test(value) || /^\+\d{10,15}$/.test(value)) return '';
      return 'Geçerli bir telefon numarası girin (örn. 0532 123 45 67 veya +90 532 123 45 67).';

    case 'birthDate': {
      if (!isValidISODate(value)) return 'Geçerli bir tarih girin.';
      if (value > todayISO()) return 'Doğum tarihi gelecekte olamaz.';
      const age = ageOn(value);
      if (age < MIN_AGE) return `En az ${MIN_AGE} yaşında olmalısınız.`;
      if (age > MAX_AGE) return 'Doğum tarihi geçersiz görünüyor, lütfen kontrol edin.';
      return '';
    }

    case 'classYear':
      // Eski kayıtlardaki serbest metin değerler değişmedikçe kabul edilir
      if (CLASS_YEARS.includes(value) || value === saved?.classYear) return '';
      return 'Listeden bir sınıf seçin.';

    default:
      if (value.length < 2) return 'En az 2 karakter olmalı.';
      if (value.length > 100) return 'En fazla 100 karakter olabilir.';
      if (!TEXT_RE.test(value)) return 'Yalnızca harf, rakam ve . , - ( ) & / karakterleri kullanılabilir.';
      return '';
  }
};

// Tüm düzenlenebilir alanları doğrular
const validateAll = (values, saved, roleLocked) => {
  const errors = {};
  EDITABLE_KEYS.forEach((k) => {
    if (k === 'userRole' && roleLocked) return; // kilitli rol kullanıcıdan gelmiyor
    const msg = validateField(k, values[k] || '', values, saved);
    if (msg) errors[k] = msg;
  });
  return errors;
};

/* ------------------------------------------------------------------ */
/* Sayfa                                                               */
/* ------------------------------------------------------------------ */
function Profile() {
  const [user, setUser] = useState({
    id: '',
    email: '',
    first_name: '',
    last_name: '',
    school: '',
    class: '',
    phoneNumber: '',
    birthDate: '',
    university: '',
    faculty: '',
    department: '',
    classYear: '',
    userRole: '',
    groups: [],
    profilePhoto: null,
    lastLogin: '',
  });
  const [saved, setSaved] = useState(null); // sunucudaki son hâl
  const [loading, setLoading] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwKey, setPwKey] = useState(0); // başarılı güncellemeden sonra alanları sıfırlar

  const [fieldErrors, setFieldErrors] = useState({});
  const [loadError, setLoadError] = useState('');
  const [profileError, setProfileError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [photoError, setPhotoError] = useState('');
  const [notification, setNotification] = useState('');

  const toastTimer = useRef(null);
  const navigate = useNavigate();
  const token = localStorage.getItem('token');

  const showToast = useCallback((message) => {
    setNotification(message);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setNotification(''), 3500);
  }, []);

  useEffect(() => () => clearTimeout(toastTimer.current), []);

  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!token) {
        navigate('/login');
        return;
      }

      try {
        const decoded = jwtDecode(token);
        const res = await fetch(`${API_URL}/api/users/${decoded.userId}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });

        const data = await res.json();
        if (!res.ok) {
          if (res.status === 401) {
            navigate('/login');
            return;
          }
          throw new Error(data.error || 'Profil yüklenemedi');
        }
        let photo = data.profilePhoto || null;
        try {
          photo = localStorage.getItem(`profilePhoto_${decoded.userId}`) || photo;
        } catch (e) {
          /* localStorage kullanılamıyor: sunucudaki fotoğraf kullanılır */
        }
        const lastLogin = data.lastLogin ? new Date(data.lastLogin).toLocaleString('tr-TR', {
          day: 'numeric',
          month: 'long',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        }) : 'Belirtilmemiş';
        const loaded = { ...data, birthDate: toDateInput(data.birthDate), profilePhoto: photo, lastLogin };
        setUser(loaded);
        setSaved(loaded);
        setLoadError('');
      } catch (err) {
        setLoadError('Profil yüklenirken bir hata oluştu: ' + err.message);
        console.error('Fetch error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchUserProfile();
  }, [token, navigate]);

  const isDirty = !!saved && EDITABLE_KEYS.some((k) => (user[k] || '') !== (saved[k] || ''));
  // Rol daha önce atanmışsa kullanıcı değiştiremez (asıl kontrol sunucuda da yapılmalı)
  const roleLocked = LOCK_ROLE_AFTER_SET && !!saved?.userRole;
  const isStudent = user.userRole === 'Öğrenci';

  // Kaydedilmemiş değişiklik varken sekmeyi kapatmaya karşı uyarı
  useEffect(() => {
    if (!isDirty) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [isDirty]);

  const updateProfile = async (e) => {
    e.preventDefault();
    if (savingProfile) return;
    setProfileError('');

    if (!token) {
      setProfileError('Oturum bilgisi bulunamadı. Lütfen giriş yapın.');
      return;
    }
    if (!user.id) {
      setProfileError('Kullanıcı kimliği bulunamadı. Lütfen sayfayı yenileyin.');
      return;
    }

    // Boşlukları temizle, sonra doğrula
    const cleaned = { ...user };
    EDITABLE_KEYS.forEach((k) => { cleaned[k] = normalizeField(k, user[k]); });
    if (roleLocked) cleaned.userRole = saved.userRole;

    const errors = validateAll(cleaned, saved, roleLocked);
    setFieldErrors(errors);
    setUser(cleaned);
    const firstError = EDITABLE_KEYS.find((k) => errors[k]);
    if (firstError) {
      setProfileError('Lütfen işaretli alanları düzeltin.');
      const el = document.getElementById(`f-${firstError}`);
      if (el) el.focus();
      return;
    }

    setSavingProfile(true);
    try {
      const payload = {
        email: cleaned.email,
        firstName: cleaned.first_name,
        lastName: cleaned.last_name,
        school: cleaned.school,
        class: cleaned.class,
        phoneNumber: cleaned.phoneNumber || undefined,
        birthDate: cleaned.birthDate || undefined,
        university: cleaned.university || undefined,
        faculty: cleaned.faculty || undefined,
        department: cleaned.department || undefined,
        classYear: cleaned.classYear || undefined,
      };
      // Kilitli rol sunucuya hiç gönderilmez
      if (!roleLocked) payload.userRole = cleaned.userRole;

      const res = await fetch(`${API_URL}/api/users/${user.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Profil güncellenemedi');
      }
      setSaved({ ...cleaned });
      showToast('Profil başarıyla güncellendi.');
    } catch (err) {
      setProfileError('Profil güncellenirken bir hata oluştu: ' + err.message);
    } finally {
      setSavingProfile(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setUser((prev) => ({ ...prev, [name]: value }));
    setProfileError('');
    // Düzenlenen alanın hatasını kaldır; rol değişirse zorunlu alan hataları yeniden hesaplanır
    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next[name];
      if (name === 'userRole') STUDENT_REQUIRED.forEach((k) => delete next[k]);
      return next;
    });
  };

  // Alandan çıkınca: boşlukları düzelt ve doğrula
  const handleBlur = (e) => {
    const { name, value } = e.target;
    const clean = normalizeField(name, value);
    if (clean !== value) setUser((prev) => ({ ...prev, [name]: clean }));
    const msg = validateField(name, clean, { ...user, [name]: clean }, saved);
    setFieldErrors((prev) => {
      const next = { ...prev };
      if (msg) next[name] = msg; else delete next[name];
      return next;
    });
  };

  const resetChanges = () => {
    if (!saved) return;
    setUser((prev) => {
      const next = { ...prev };
      EDITABLE_KEYS.forEach((k) => { next[k] = saved[k] || ''; });
      return next;
    });
    setFieldErrors({});
    setProfileError('');
  };

  const handlePhotoUpload = (event) => {
    const input = event.target;
    const file = input.files[0];
    if (!file) return;
    setPhotoError('');

    if (!file.type.startsWith('image/')) {
      setPhotoError('Lütfen bir görsel dosyası seçin.');
      input.value = '';
      return;
    }
    if (file.size > MAX_PHOTO_SIZE) {
      setPhotoError('Fotoğraf 5 MB’tan büyük olamaz.');
      input.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onerror = () => setPhotoError('Fotoğraf okunamadı. Başka bir dosya deneyin.');
    reader.onloadend = async () => {
      const photoData = reader.result;
      if (!photoData) return;
      setUser((prev) => ({ ...prev, profilePhoto: photoData }));
      try {
        localStorage.setItem(`profilePhoto_${user.id}`, photoData);
      } catch (e) {
        /* Depolama dolu olabilir; sunucuya yükleme yine denenir */
      }

      setPhotoBusy(true);
      try {
        const formData = new FormData();
        formData.append('profilePhoto', file);
        const res = await fetch(`${API_URL}/api/users/${user.id}/photo`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });
        if (!res.ok) throw new Error(`Hata: ${res.status}`);
        showToast('Profil fotoğrafı güncellendi.');
      } catch (err) {
        console.error('Backend güncellemesi başarısız:', err);
        setPhotoError('Fotoğraf bu cihazda gösteriliyor ancak sunucuya yüklenemedi. Daha sonra tekrar deneyin.');
      } finally {
        setPhotoBusy(false);
        input.value = '';
      }
    };
    reader.readAsDataURL(file);
  };

  const handlePhotoRemove = async () => {
    setPhotoError('');
    setUser((prev) => ({ ...prev, profilePhoto: null }));
    try {
      localStorage.removeItem(`profilePhoto_${user.id}`);
    } catch (e) {
      /* yoksay */
    }

    setPhotoBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/users/${user.id}/photo`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Hata: ${res.status}`);
      showToast('Profil fotoğrafı kaldırıldı.');
    } catch (err) {
      console.error('Backend güncellemesi başarısız:', err);
      setPhotoError('Fotoğraf sunucudan kaldırılamadı. Daha sonra tekrar deneyin.');
    } finally {
      setPhotoBusy(false);
    }
  };

  // Şifre kuralları: anlık gösterilir
  const rules = [
    { ok: newPassword.length >= 8, label: 'En az 8 karakter' },
    { ok: /\p{L}/u.test(newPassword), label: 'En az bir harf' },
    { ok: /\d/.test(newPassword), label: 'En az bir rakam' },
    { ok: !!confirmPassword && newPassword === confirmPassword, label: 'Şifreler eşleşiyor' },
  ];

  const handlePasswordUpdate = async (e) => {
    e.preventDefault();
    if (savingPassword) return;
    setPasswordError('');

    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError('Tüm alanlar doldurulmalı.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('Yeni şifreler eşleşmiyor.');
      return;
    }
    if (newPassword.length < 8) {
      setPasswordError('Yeni şifre en az 8 karakter olmalı.');
      return;
    }
    if (!/\p{L}/u.test(newPassword) || !/\d/.test(newPassword)) {
      setPasswordError('Yeni şifre en az bir harf ve bir rakam içermeli.');
      return;
    }
    if (newPassword === currentPassword) {
      setPasswordError('Yeni şifre mevcut şifreyle aynı olamaz.');
      return;
    }
    if (!token) {
      setPasswordError('Oturum bilgisi bulunamadı. Lütfen giriş yapın.');
      return;
    }
    if (!user.id) {
      setPasswordError('Kullanıcı kimliği bulunamadı. Lütfen sayfayı yenileyin.');
      return;
    }

    setSavingPassword(true);
    try {
      const res = await fetch(`${API_URL}/api/users/${user.id}/password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ currentPassword, newPassword }),
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Şifre güncellenemedi');
      }
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setPwKey((k) => k + 1); // göz simgesi durumlarını da sıfırla
      showToast('Şifreniz başarıyla güncellendi.');
    } catch (err) {
      setPasswordError('Şifre güncellenirken bir hata oluştu: ' + err.message);
      console.error('Password update error:', err);
    } finally {
      setSavingPassword(false);
    }
  };

  const fullName = `${user.first_name || ''} ${user.last_name || ''}`.trim();
  const initials = `${(user.first_name || '')[0] || ''}${(user.last_name || '')[0] || ''}`.toUpperCase();

  // Tek bir form alanını çizer (input veya select)
  const renderField = (f) => {
    const err = fieldErrors[f.name];
    const locked = f.disabled || (f.name === 'userRole' && roleLocked);
    const required = f.name === 'userRole' || (isStudent && STUDENT_REQUIRED.includes(f.name));
    const common = {
      id: `f-${f.name}`,
      className: `pf-input${err ? ' is-invalid' : ''}`,
      name: f.name,
      value: user[f.name] || '',
      onChange: handleChange,
      onBlur: handleBlur,
      disabled: locked,
      'aria-invalid': err ? true : undefined,
      'aria-describedby': err ? `err-${f.name}` : undefined,
    };

    let control;
    if (loading) {
      control = <span className="pf-sk pf-sk-input" />;
    } else if (f.options) {
      // Eski kayıttaki listede olmayan değer görünmeye devam etsin
      const current = user[f.name];
      const options = current && !f.options.includes(current) ? [current, ...f.options] : f.options;
      control = (
        <select {...common}>
          <option value="">{f.placeholder}</option>
          {options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      );
    } else {
      control = (
        <input
          {...common}
          type={f.type || 'text'}
          placeholder={f.placeholder || ''}
          autoComplete={f.autoComplete || 'off'}
          inputMode={f.inputMode}
          maxLength={TEXT_KEYS.includes(f.name) ? 100 : undefined}
          min={f.type === 'date' ? yearsAgoISO(MAX_AGE) : undefined}
          max={f.type === 'date' ? todayISO() : undefined}
        />
      );
    }

    return (
      <label key={f.name} className={`pf-field${f.full ? ' full' : ''}`}>
        <span className="pf-label">
          {f.label}
          {required && !locked && <span className="pf-req" aria-hidden="true"> *</span>}
        </span>
        {control}
        {err && <span id={`err-${f.name}`} className="pf-field-error" role="alert">{err}</span>}
      </label>
    );
  };

  return (
    <div className="pf-page">
      <div className="pf-toast-zone" role="status" aria-live="polite">
        {notification && <div className="pf-toast">{notification}</div>}
      </div>

      {/* Başlık */}
      <header className="pf-head">
        <div className="pf-container pf-head-inner">
          <label className={`pf-avatar${photoBusy ? ' is-busy' : ''}`} htmlFor="photo-upload" title="Fotoğrafı değiştir">
            {user.profilePhoto ? (
              <img src={user.profilePhoto} alt="Profil fotoğrafı" />
            ) : (
              <span>{initials || <Icon name="user" className="pf-avatar-icon" />}</span>
            )}
            <span className="pf-avatar-cam"><Icon name="camera" className="pf-avatar-cam-icon" /></span>
          </label>

          <div className="pf-head-copy">
            <h1>
              {loading ? <span className="pf-sk pf-sk-name" /> : fullName ? <span className="pf-mark">{fullName}</span> : 'Profil bilgileriniz'}
            </h1>
            <p className="pf-sub">Kişisel ve akademik bilgilerinizi yönetin, güncel tutun.</p>

            <div className="pf-meta">
              {saved?.userRole && <span className="pf-tag">{saved.userRole}</span>}
              {user.lastLogin && (
                <span className="pf-meta-item">
                  <Icon name="clock" className="pf-meta-icon" />
                  Son giriş: {user.lastLogin}
                </span>
              )}
            </div>

            <div className="pf-photo-actions">
              <input
                type="file"
                accept="image/*"
                onChange={handlePhotoUpload}
                className="pf-file-input"
                id="photo-upload"
                disabled={loading || photoBusy}
              />
              <label htmlFor="photo-upload" className={`pf-btn pf-btn-solid${photoBusy ? ' is-disabled' : ''}`}>
                {photoBusy ? 'İşleniyor...' : user.profilePhoto ? 'Fotoğrafı değiştir' : 'Fotoğraf yükle'}
              </label>
              {user.profilePhoto && (
                <button type="button" onClick={handlePhotoRemove} className="pf-btn pf-btn-outline" disabled={photoBusy}>
                  Fotoğrafı kaldır
                </button>
              )}
            </div>
            {photoError && <p className="pf-error pf-error-inline" role="alert">{photoError}</p>}
          </div>
        </div>
      </header>

      <main className="pf-container pf-main">
        {loadError && (
          <div className="pf-error" role="alert">
            <span>{loadError}</span>
            <button type="button" className="pf-link-btn" onClick={() => window.location.reload()}>Sayfayı yenile</button>
          </div>
        )}

        <div className="pf-grid">
          {/* Kişisel bilgiler */}
          <form className="pf-card" onSubmit={updateProfile} noValidate>
            <h2 className="pf-card-title">
              <span className="pf-card-icon"><Icon name="user" className="pf-card-svg" /></span>
              Kişisel ve akademik bilgiler
            </h2>

            <div className="pf-fields">
              {FIELDS.map(renderField)}
            </div>

            <p className="pf-hint">
              Ad, soyad ve e-posta bu sayfadan değiştirilemez.
              {roleLocked && ' Rolünüz atandığı için değiştirilemez; değişiklik için yönetici ile iletişime geçin.'}
              {!roleLocked && isStudent && ' Öğrenci rolünde üniversite, fakülte, bölüm ve sınıf zorunludur.'}
            </p>

            {profileError && <p className="pf-error" role="alert">{profileError}</p>}

            <div className={`pf-savebar${isDirty ? ' is-dirty' : ''}`}>
              {isDirty && <span className="pf-savebar-note">Kaydedilmemiş değişiklikler var</span>}
              <div className="pf-savebar-actions">
                {isDirty && (
                  <button type="button" className="pf-btn pf-btn-outline" onClick={resetChanges} disabled={savingProfile}>
                    Vazgeç
                  </button>
                )}
                <button type="submit" className="pf-btn pf-btn-solid" disabled={loading || savingProfile || !isDirty}>
                  {savingProfile ? 'Kaydediliyor...' : 'Profilimi güncelle'}
                </button>
              </div>
            </div>
          </form>

          {/* Şifre */}
          <form className="pf-card" onSubmit={handlePasswordUpdate} noValidate>
            <h2 className="pf-card-title">
              <span className="pf-card-icon"><Icon name="lock" className="pf-card-svg" /></span>
              Şifre güncelle
            </h2>

            <div className="pf-fields single" key={pwKey}>
              <PasswordField
                id="pw-current"
                label="Mevcut şifre"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                autoComplete="current-password"
              />
              <PasswordField
                id="pw-new"
                label="Yeni şifre"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoComplete="new-password"
              />
              <PasswordField
                id="pw-confirm"
                label="Yeni şifre tekrar"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
              />
            </div>

            <ul className="pf-rules" aria-label="Şifre kuralları">
              {rules.map((r) => (
                <li key={r.label} className={r.ok ? 'is-ok' : ''}>
                  <Icon name={r.ok ? 'check' : 'dot'} className="pf-rule-icon" />
                  {r.label}
                </li>
              ))}
            </ul>

            {passwordError && <p className="pf-error" role="alert">{passwordError}</p>}

            <button type="submit" className="pf-btn pf-btn-solid pf-btn-block" disabled={savingPassword}>
              {savingPassword ? 'Güncelleniyor...' : 'Şifreyi güncelle'}
            </button>
          </form>
        </div>

        <div className="pf-back">
          <Link to="/" className="pf-back-link">← Ana sayfaya dön</Link>
        </div>
      </main>
    </div>
  );
}

export default Profile;